import hljs from "highlight.js"
import "highlight.js/styles/atom-one-dark.css"
import { useCurrentId } from "hooks/useCurrentId.ts"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Link,
  Typography,
} from "@strapi/design-system"
import { ArrowLeft } from "@strapi/icons"

interface CodeTemplateProps {
  text: string
  code: string
}

const CodeTemplate = ({ text, code }: CodeTemplateProps) => {
  const { t } = useTranslation()
  return (
    <Box marginTop={4}>
      <Typography>{text}</Typography>
      <Box style={{ position: "relative" }} marginTop={2}>
        <pre>
          <code className="language-xml">{code}</code>
        </pre>
        <Button
          variant="secondary"
          onClick={() => navigator.clipboard.writeText(code)}
          size="S"
          style={{ position: "absolute", top: 8, right: 8 }}
        >
          {t("copy")}
        </Button>
      </Box>
    </Box>
  )
}
export const IntegrationInfo = () => {
  const { t } = useTranslation()
  const service = getTintVTOService()
  const merchantId = useCurrentId()

  const contentLayoutRef = useRef(null)

  const codeBlocks = useMemo(
    () => [
      {
        text: t("integrationInfo.addScriptImport"),
        code: `<script type="module" src="https://tintvto.com/widget.js"></script>`,
      },
      {
        text: t("integrationInfo.addToIndexHtml"),
        code: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width" />
    <title>Your Website Title</title>

    <!-- import of TINT-vto widget -->
    <script type="module" src="tint-vto/widget/index.js"></script>
  </head>
  <body></body>
</html>`,
      },
      {
        text: t("integrationInfo.runWidget"),
        code: `<tint-vto merchant-id="${merchantId}" />`,
      },
      {
        text: t("integrationInfo.openMethodDescription"),
        code: `<script type="module">
  !(async () => {
    const widget = document.querySelector("tint-vto")
    await widget.open()
  })()
</script>`,
      },
    ],
    [merchantId, t]
  )

  const [merchantName, setMerchantName] = useState("")

  useEffect(() => {
    ;(async () => {
      const merchantData = await service.merchantById(merchantId)
      setMerchantName(
        t("integrationInfo.merchantName", { name: merchantData.data.merchantById.name })
      )
    })()
  }, [merchantId, t])

  useEffect(() => {
    if (contentLayoutRef && contentLayoutRef.current)
      contentLayoutRef.current
        .querySelectorAll("pre code")
        .forEach((block) => hljs.highlightElement(block))
  }, [contentLayoutRef])

  return (
    <Box>
      <BaseHeaderLayout
        title={`${t("integrationInfo.title")} ${merchantName}`}
        subtitle={t("integrationInfo.subtitle")}
        as={"h1"}
        navigationAction={
          <Link startIcon={<ArrowLeft />} to={`/merchants`}>
            {t("integrationInfo.backToMerchants")}
          </Link>
        }
      />
      <ContentLayout>
        <Box ref={contentLayoutRef}>
          {codeBlocks.map((data, i) => (
            <CodeTemplate key={i} {...data} />
          ))}
        </Box>
      </ContentLayout>
    </Box>
  )
}
